import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PricingTransparency = () => {
  const [selectedService, setSelectedService] = useState('data-strategy');

  const pricingData = {
    'data-strategy': {
      title: 'Data Strategy Development',
      dayRate: 1500,
      typicalDays: '10-15 days',
      priceRange: '€15,000 - €25,000',
      scope: [
        'Current state assessment',
        'Strategic roadmap development',
        'Technology architecture planning',
        'Implementation timeline',
        'ROI projections',
        'Stakeholder alignment sessions'
      ],
      deliverables: [
        'Comprehensive data strategy document',
        'Implementation roadmap',
        'Technology recommendations',
        'Budget planning framework',
        'Success metrics definition'
      ]
    },
    'analytics-implementation': {
      title: 'Analytics Implementation',
      dayRate: 1500,
      typicalDays: '20-30 days',
      priceRange: '€30,000 - €45,000',
      scope: [
        'Platform selection and setup',
        'Data pipeline development',
        'Dashboard and report creation',
        'User training and adoption',
        'Performance optimization',
        'Documentation and handover'
      ],
      deliverables: [
        'Fully configured analytics platform',
        'Custom dashboards and reports',
        'Data pipeline documentation',
        'User training materials',
        'Maintenance procedures'
      ]
    },
    'governance-framework': {
      title: 'Governance Framework Design',
      dayRate: 1500,
      typicalDays: '8-12 days',
      priceRange: '€12,000 - €18,000',
      scope: [
        'Governance policy development',
        'Data quality standards',
        'Compliance framework setup',
        'Role and responsibility matrix',
        'Monitoring and audit procedures',
        'Training program design'
      ],
      deliverables: [
        'Data governance policy document',
        'Quality standards framework',
        'Compliance procedures',
        'Governance dashboard',
        'Training curriculum'
      ]
    },
    'team-training': {
      title: 'Team Training & Development',
      dayRate: 2500,
      typicalDays: '5-10 days',
      priceRange: '€12,500 - €25,000',
      scope: [
        'Skills assessment',
        'Customized curriculum development',
        'Hands-on training sessions',
        'Best practices workshops',
        'Mentoring and coaching',
        'Progress evaluation'
      ],
      deliverables: [
        'Skills assessment report',
        'Training materials and resources',
        'Certification program',
        'Ongoing support framework',
        'Performance metrics'
      ]
    },
    'fractional-cdo': {
      title: 'Fractional CDO Service',
      dayRate: 2000,
      typicalDays: 'Ongoing engagement',
      priceRange: '€10,000/month',
      scope: [
        'Strategic planning and oversight',
        'Executive reporting',
        'Team mentoring',
        'Vendor management',
        'Crisis management',
        'Board-level presentations'
      ],
      deliverables: [
        'Monthly strategic reports',
        'Quarterly board presentations',
        'Team development plans',
        'Vendor evaluation reports',
        'Crisis response procedures'
      ]
    }
  };

  const services = [
    { id: 'data-strategy', name: 'Data Strategy', icon: 'Target' },
    { id: 'analytics-implementation', name: 'Analytics Implementation', icon: 'BarChart3' },
    { id: 'governance-framework', name: 'Governance Framework', icon: 'Shield' },
    { id: 'team-training', name: 'Team Training', icon: 'Users' },
    { id: 'fractional-cdo', name: 'Fractional CDO', icon: 'Crown' }
  ];

  const currentService = pricingData?.[selectedService];

  return (
    <div className="bg-white rounded-xl shadow-elevation border border-border p-8">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-primary mb-4">
          Transparent Pricing & Project Scope
        </h3>
        <p className="text-muted-foreground max-w-2xl mx-auto">
          Clear day rates and project scope examples to help you plan your investment. All prices include comprehensive deliverables and ongoing support.
        </p>
      </div>
      <div className="flex flex-wrap justify-center gap-2 mb-8">
        {services?.map((service) => (
          <button
            key={service?.id}
            onClick={() => setSelectedService(service?.id)}
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
              selectedService === service?.id
                ? 'bg-secondary text-white' :'bg-surface text-muted-foreground hover:text-primary hover:bg-secondary/10'
            }`}
          >
            <Icon name={service?.icon} size={16} />
            <span>{service?.name}</span>
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="bg-surface rounded-lg p-6 mb-6">
            <h4 className="text-lg font-semibold text-primary mb-4">{currentService?.title}</h4>
            
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="text-center p-4 bg-white rounded-lg border border-border">
                <div className="text-2xl font-bold text-secondary mb-1">
                  €{currentService?.dayRate?.toLocaleString()}
                </div>
                <div className="text-sm text-muted-foreground">Per day rate</div>
              </div>
              <div className="text-center p-4 bg-white rounded-lg border border-border">
                <div className="text-2xl font-bold text-primary mb-1">
                  {currentService?.typicalDays}
                </div>
                <div className="text-sm text-muted-foreground">Typical duration</div>
              </div>
            </div>

            <div className="bg-primary/5 rounded-lg p-4 border border-primary/10">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Calculator" size={20} className="text-primary" />
                <span className="font-semibold text-primary">Total Investment Range</span>
              </div>
              <div className="text-2xl font-bold text-secondary">{currentService?.priceRange}</div>
              <div className="text-sm text-muted-foreground mt-1">
                Includes all deliverables and 30-day post-project support
              </div>
            </div>
          </div>

          <div className="bg-surface rounded-lg p-6">
            <h5 className="font-semibold text-primary mb-4 flex items-center space-x-2">
              <Icon name="CheckCircle" size={20} className="text-secondary" />
              <span>Project Scope Includes</span>
            </h5>
            <div className="space-y-2">
              {currentService?.scope?.map((item, index) => (
                <div key={index} className="flex items-start space-x-2">
                  <Icon name="Check" size={16} className="text-secondary mt-0.5" />
                  <span className="text-muted-foreground text-sm">{item}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div>
          <div className="bg-surface rounded-lg p-6 mb-6">
            <h5 className="font-semibold text-primary mb-4 flex items-center space-x-2">
              <Icon name="Package" size={20} className="text-secondary" />
              <span>Key Deliverables</span>
            </h5>
            <div className="space-y-3">
              {currentService?.deliverables?.map((deliverable, index) => (
                <div key={index} className="flex items-start space-x-3 p-3 bg-white rounded-lg border border-border">
                  <div className="p-1 bg-secondary/10 rounded">
                    <Icon name="FileText" size={16} className="text-secondary" />
                  </div>
                  <span className="text-muted-foreground text-sm">{deliverable}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-secondary/5 rounded-lg p-6 border border-secondary/20">
            <div className="flex items-start space-x-3 mb-4">
              <Icon name="Info" size={20} className="text-secondary mt-0.5" />
              <div>
                <h6 className="font-semibold text-primary mb-2">What's Included</h6>
                <ul className="text-sm text-muted-foreground space-y-1">
                  <li>• Initial consultation and scoping</li>
                  <li>• Regular progress updates</li>
                  <li>• Stakeholder presentations</li>
                  <li>• 30-day post-delivery support</li>
                  <li>• Documentation and handover</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-8 pt-8 border-t border-border">
        <div className="text-center">
          <h6 className="font-semibold text-primary mb-4">Ready to Get Started?</h6>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="default"
              iconName="Calendar"
              iconPosition="left"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 active:scale-95 transition-all duration-200"
              onClick={() => window.location.href = '/contact-multi-channel-engagement-hub'}
            >
              Schedule Consultation
            </Button>
            <Button
              variant="outline"
              iconName="Calculator"
              iconPosition="left"
              className="hover:scale-105 active:scale-95 transition-all duration-200"
              onClick={() => window.location.href = '/roi-calculator-suite-value-demonstration-tools'}
            >
              Calculate Your ROI
            </Button>
            <Button
              variant="ghost"
              iconName="Download"
              iconPosition="left"
            >
              Download Pricing Guide
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingTransparency;